import React, { useState, useEffect } from "react";
import {
  HStack,
  Box,
  Stack,
  VStack,
  Text,
  Button,
  Input,
  Radio,
  Divider,
  Pressable,
} from "native-base";
import { useWindowDimension } from "../../../Utility/WindowDimensions";
import { Loc } from "../../../App/Loc";
import AutoTranslation from "../../../components/header/AccountSettings/AutoTranslation";
import * as AV from "../../../AVCore/AVCore";
import { AVBase } from "../../../AVCore/AVBase";
import { RootStateOrAny, useDispatch, useSelector } from "react-redux";
import { setConvo } from "../../../Redux/Actions/convoActions";
import Recipients from "../../chat/recipients/Recipients";

const ManageConvoPopup = ({
  onClose,
  convo,
  currentUser,
  toFK,
  setToFK,
  userConvo,
  setConvos,
  setShowConvoListModal,
  setMdlConvoVisible,
  setMdlCreateConvoVisible,
  setConvoLoading,
  setIsOpenConvo,
}) => {
  const dimensions = useWindowDimension();
  const isMobile = dimensions?.width <= 500 || false;
  const [subject, setSubject] = useState(convo?.subject || "");
  const [enabled, setEnabled] = useState(convo?.enabled);
  const [isLoading, setIsLoading] = useState(false);
  const dispatch = useDispatch();
  const [showRecipientsPopup, setShowRecipientsPopup] = useState(false);
  const [recipientCount, setRecipientCount] = useState(
    convo?.people?.length || 0,
  );
  //convo?.people?.filter((x) => x?.id !== currentUser?.id)?.length || 0;
  const [addedPeople, setAddedPeople] = useState<Person[]>([]);
  const [removedPeople, setRemovedPeople] = useState<Person[]>([]);
  const entityDataList = useSelector(
    (state: RootStateOrAny) => state.user.entitydatalist,
  );
  const [sharedPlan, setSharedPlan] = React.useState<AV.ISharedPlan>();
  const [entity, setEntity] = useState(currentUser);

  interface Person {
    id: string;
    title: string;
    thumb?: string;
    type?: string;
  }

  useEffect(() => {
    // Initialize subject when convo changes
    if (convo?.subject) {
      setSubject(convo.subject);
    }

    // Initialize toFK with current people and reset tracking arrays
    if (convo?.people) {
      const updatedPeople = [
        ...convo.people.filter((p) => p?.id !== currentUser?.id),
        convo.people.find((p) => p?.id === currentUser?.id),
      ].filter(Boolean); // remove undefined if user is not found

      setToFK(updatedPeople);
      setAddedPeople([]);
      setRemovedPeople([]);
    }
  }, [convo]);

  useEffect(() => {
    let isMounted = true;

    const loadPlan = async () => {
      try {
        const sharedPlan: AV.ISharedPlan = await AV.AVEntity.getSharedPlan(
          AV.AVEntity.getFk(currentUser),
        );
        if (isMounted) {
          setSharedPlan(sharedPlan);
        }
      } catch (error) {
        if (isMounted) console.error("Failed to load sharedPlan:", error);
      }
    };

    loadPlan();

    return () => {
      isMounted = false;
    };
  }, [currentUser]);

  const handleUpdate = async () => {
    try {
      setIsLoading(true);
      let updateConvo: any;

      // Handle people removal
      if (removedPeople?.length > 0) {
        const returnvalue = await AV.Convo.removePeople(convo, removedPeople);
        if (returnvalue) {
          // Update local state with removed people
          const updatedConvo = {
            ...convo,
            people: convo.people.filter(
              (person) => !removedPeople.some((rp) => rp.id === person.id),
            ),
          };
        }
      }

      // Handle adding new people
      if (addedPeople?.length > 0) {
        const returnvalue = await AV.Convo.addPeople(convo, addedPeople);
        if (returnvalue) {
          // Update local state with new people
          const updatedConvo = {
            ...convo,
            people: [...convo.people, ...addedPeople],
          };
        }
      }

      convo.enabled = enabled;
      convo.subject = convo?.subject !== subject ? subject : convo.subject;
      const convoFk: any = AVBase.getFk(convo);
      convoFk.enabled = enabled;
      convoFk.subject = convo?.subject !== subject ? subject : convo.subject;
      updateConvo = await AV.Convo.update(convoFk);

      if (updateConvo) {
        const updatedConvo = {
          ...convo,
          subject: subject,
          enabled: convoFk.enabled,
          posts: (convo as any).children ?? (convo as any).posts,
        };

        // Update the conversation in Redux store
        const updatedUserConvo = userConvo.map((conv) =>
          conv.id === updatedConvo.id ? updatedConvo : conv,
        );
        dispatch(setConvo(updatedUserConvo));
        setConvos(updatedConvo);
      }

      // Reset tracking arrays after successful update
      setAddedPeople([]);
      setRemovedPeople([]);
      onClose();
    } catch (error) {
      console.error("Error updating conversation:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box
      position="fixed"
      top="0"
      left="0"
      right="0"
      bottom="0"
      bg="rgba(0, 0, 0, 0.5)"
      zIndex={1000}
      display="flex"
      justifyContent="center"
      alignItems="center"
    >
      {/* Main Dialog */}
      <Box
        width={isMobile ? `${dimensions.width - 15}px` : "600px"}
        bg="white"
        borderRadius="8px"
        shadow={2}
        display="flex"
        flexDirection="column"
      >
        {/* Header */}
        <HStack
          justifyContent="space-between"
          alignItems="center"
          px={4}
          py={3}
          borderBottomWidth="2px"
          borderBottomColor="gray.100"
        >
          <Text fontSize="20px" fontWeight="600">
            {Loc.currentLang.manageConversation.manageConversation}
          </Text>
        </HStack>

        {/* Content */}
        <Box flex={1} overflowY="auto">
          <VStack p={5} space={4}>
            {convo?.subject?.trim()?.length >= 1 && (
              <>
                {/* Recipients */}
                <HStack justifyContent="space-between" alignItems="center">
                  <Text fontSize="16px" fontWeight="500">
                    {Loc.currentLang.manageConversation.recipients}
                  </Text>
                  <Pressable onPress={() => setShowRecipientsPopup(true)}>
                    <Text color="#00A7BC" fontSize="14px">
                      {`${recipientCount - 1}+ ${Loc.currentLang.manageConversation.recipients}`}
                    </Text>
                  </Pressable>
                </HStack>

                {/* Recipients Popup Overlay */}
                {showRecipientsPopup && (
                  <Box
                    position="fixed"
                    top={0}
                    left={0}
                    right={0}
                    bottom={0}
                    bg="rgba(0, 0, 0, 0.7)"
                    zIndex={2000}
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                  >
                    <Box
                      bg="white"
                      borderRadius="8px"
                      shadow="lg"
                      width={isMobile ? "90%" : "400px"}
                      maxHeight="90vh"
                      overflow="hidden"
                    >
                      <Recipients
                        removedPeople={removedPeople}
                        addedPeople={addedPeople}
                        toFK={toFK}
                        setRemovedPeople={setRemovedPeople}
                        setAddedPeople={setAddedPeople}
                        setShowRecipientsPopup={setShowRecipientsPopup}
                        setRecipientCount={setRecipientCount}
                        setShowConvoListModal={setShowConvoListModal}
                        setMdlConvoVisible={setMdlConvoVisible}
                        setMdlCreateConvoVisible={setMdlCreateConvoVisible}
                        setConvoLoading={setConvoLoading}
                        setIsOpenConvo={setIsOpenConvo}
                      />
                    </Box>
                  </Box>
                )}
                <Divider
                  marginY={"10px"}
                  marginLeft={"3px"}
                  width={"100%"}
                  zIndex={-1}
                />

                {/* Subject */}
                <HStack
                  justifyContent="space-between"
                  alignItems="center"
                  space={4}
                >
                  <Text fontSize="16px" fontWeight="500">
                    {Loc.currentLang.menu.subject}
                  </Text>
                  <Input
                    flex={1}
                    value={subject}
                    onChangeText={(text) => setSubject(text.trim())}
                    placeholder={Loc.currentLang.menu.enterSubject}
                    borderRadius="4px"
                    borderColor="gray.300"
                    _focus={{
                      borderColor: "#00A7BC",
                    }}
                    maxLength={50}
                    height="40px"
                  />
                </HStack>

                {/* Enabled */}
                <HStack justifyContent="flex-start" alignItems="center">
                  <Text fontSize="16px" fontWeight="500">
                    {Loc.currentLang.manageConversation.enabled}
                  </Text>

                  <HStack
                    space={isMobile ? "30px" : "65px"}
                    alignItems="center"
                    left="10px"
                  >
                    {["true", "false"].map((val) => (
                      <Pressable
                        key={val}
                        onPress={() => setEnabled(val === "true")}
                      >
                        <HStack alignItems="center" space={2}>
                          {/* Circle */}
                          <Box
                            width="18px"
                            height="18px"
                            borderRadius="50%"
                            borderWidth="2px"
                            borderColor="#00A3C4"
                            alignItems="center"
                            justifyContent="center"
                          >
                            {enabled === (val === "true") && (
                              <Box
                                width="10px"
                                height="10px"
                                borderRadius="50%"
                                bg="#00A3C4"
                              />
                            )}
                          </Box>

                          {/* Label */}
                          <Text fontSize="14px">
                            {val === "true"
                              ? Loc.currentLang.menu.yes
                              : Loc.currentLang.menu.no}
                          </Text>
                        </HStack>
                      </Pressable>
                    ))}
                  </HStack>
                </HStack>
                <Divider
                  marginY={"10px"}
                  marginLeft={"3px"}
                  width={"100%"}
                  zIndex={-1}
                />
              </>
            )}

            {/* Auto Translation */}
            <VStack space={0}>
              <Text fontSize="16px" fontWeight="500" mb={0}>
                {Loc.currentLang.manageConversation.autoTranslation}
              </Text>
              <AutoTranslation
                isAutoTrans={true}
                entity={currentUser}
                setEntity={setEntity}
                sharedPlan={sharedPlan}
                setSharedPlan={setSharedPlan}
                triggerBuyCredits={false}
                setTriggerBuyCredits={() => {}}
              />
            </VStack>
          </VStack>
        </Box>

        {/* Footer */}
        <Box
          mt="auto"
          borderTopWidth="2px"
          borderTopColor="gray.100"
          bg="white"
          position="sticky"
          bottom={0}
          left={0}
          right={0}
          zIndex={showRecipientsPopup ? "-1" : "-9"}
        >
          <HStack justifyContent="flex-end" space={2} p={4}>
            <Button
              rounded="full"
              height="40px"
              width="100px"
              isDisabled={isLoading}
              backgroundColor="gray.300"
              onPress={onClose}
            >
              <Text color="black">{Loc.currentLang.menu.close}</Text>
            </Button>
            {convo?.subject?.trim()?.length >= 1 && (
              <>
                <Button
                  rounded="full"
                  height="40px"
                  width="100px"
                  isLoading={isLoading}
                  isDisabled={isLoading || !subject.trim()}
                  backgroundColor="AVColor.primary"
                  onPress={handleUpdate}
                >
                  <Text fontSize="16px" color="white">
                    {Loc.currentLang.menu.update}
                  </Text>
                </Button>
              </>
            )}
          </HStack>
        </Box>
      </Box>
    </Box>
  );
};

export default ManageConvoPopup;
